import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { prettifyApiError } from "../../shared/api";
import { usePermessi, propsSenzaPermesso } from "../../shared/auth/permessi";
import { ErrorBanner } from "../../shared/ui";
import { PageHeader } from "../../shared/ui/PageHeader";
import {
  caricaTaxReport,
  fetchStorico,
  scaricaMese,
  type EsitoCaricamento,
  type RiepilogoMese,
} from "./corrispettiviApi";

const euro = (valore: string) =>
  Number(valore).toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  });

const giorno = (iso: string) => new Date(iso).toLocaleDateString("it-IT");

export function CorrispettiviAmazonPage() {
  const [storico, setStorico] = useState<RiepilogoMese[] | null>(null);
  const [esito, setEsito] = useState<EsitoCaricamento | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [carico, setCarico] = useState(false);
  const { puoScrivere } = usePermessi();
  const senzaPermesso = propsSenzaPermesso(puoScrivere("fatturazione"), "fatturazione");

  const ricarica = useCallback(async () => {
    try {
      setStorico(await fetchStorico());
    } catch (err) {
      setErrore(prettifyApiError(err));
    }
  }, []);

  useEffect(() => {
    void ricarica();
  }, [ricarica]);

  async function invia(file: File) {
    setCarico(true);
    setErrore(null);
    setEsito(null);
    try {
      setEsito(await caricaTaxReport(file));
      await ricarica();
    } catch (err) {
      setErrore(prettifyApiError(err));
    } finally {
      setCarico(false);
    }
  }

  return (
    <main className="flex-1 overflow-y-auto px-7 pb-8 pt-5 scroll-paper max-md:px-3 max-md:pt-3">
      <PageHeader
        title="Corrispettivi Amazon"
        subtitle="Il file mensile per l'amministrazione, dal tax report di Seller Central"
        meta={
          <Link to="/fatturazione" className="text-accent hover:underline">
            ← Fatturazione
          </Link>
        }
      />

      <section className="mb-5 rounded-md border border-line bg-paper p-4">
        <p className="mb-3 text-[12.5px] leading-relaxed text-ink-2">
          Scarica il tax report da <strong>Seller Central › Report › Tax Document Library</strong> e
          caricalo qui. Da un file possono uscire <strong>piu' mesi</strong>: ne nasce uno per
          ciascuno. Scaricalo a mese chiuso da qualche giorno — spedizioni e rimborsi dell'ultima
          settimana arrivano in ritardo, e un export troppo presto e' incompleto.
        </p>
        <label
          className={[
            "inline-block cursor-pointer rounded-md border border-accent bg-accent-2 px-3 py-1.5",
            "text-[12px] font-medium text-white",
            carico || senzaPermesso.disabled
              ? "pointer-events-none opacity-50"
              : "hover:brightness-95",
          ].join(" ")}
          title={senzaPermesso.title ?? "Carica il CSV taxReport scaricato da Seller Central"}
        >
          {carico ? "Elaboro..." : "Scegli il tax report (CSV)"}
          <input
            type="file"
            accept=".csv"
            className="hidden"
            aria-label="Scegli il tax report (CSV)"
            disabled={carico || senzaPermesso.disabled}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void invia(file);
              // Senza il reset, ricaricare lo STESSO file non scatena
              // onChange: il browser lo considera invariato.
              event.target.value = "";
            }}
          />
        </label>
      </section>

      <ErrorBanner message={errore} className="mb-4" />

      {esito && (
        <section className="mb-5 rounded-md border border-line bg-paper-2 p-4 text-[12.5px]">
          {esito.mesi.length === 0 ? (
            <p className="text-ink-2">
              Il file e' stato letto ({esito.righe_lette} righe) ma non conteneva{" "}
              <strong>nessuna riga con una data di spedizione valida</strong>: non e' stato generato
              alcun mese. Controlla di aver scaricato il tax report giusto.
            </p>
          ) : (
            <>
              <p className="mb-2 font-medium text-ink">
                {esito.mesi.length === 1
                  ? `1 mese generato da ${esito.righe_lette} righe`
                  : `${esito.mesi.length} mesi generati da ${esito.righe_lette} righe`}
              </p>
              <ul className="space-y-1.5">
                {esito.mesi.map((m) => (
                  <li key={m.chiave} className="text-ink-2">
                    <strong>{m.etichetta}</strong>: {euro(m.totale_positivi)} € di corrispettivi (
                    {m.positive} righe), {euro(m.totale_rimborsi)} € di rimborsi ({m.rimborsi}{" "}
                    righe). {m.gia_fatturate} righe gia' fatturate da Amazon, escluse. Date coperte{" "}
                    {giorno(m.prima_data)} → {giorno(m.ultima_data)}.
                    {m.sostituisce && (
                      <span className="text-amber">
                        {" "}
                        Ha sostituito il file generato il {giorno(m.sostituisce)}.
                      </span>
                    )}
                    {m.sostituisce_righe != null && m.righe < m.sostituisce_righe && (
                      <p className="mt-1 font-medium text-amber">
                        ⚠️ Attenzione: il file precedente aveva {m.sostituisce_righe} righe, questo
                        ne ha {m.righe}. Controlla di aver caricato il tax report giusto prima di
                        usarlo.
                      </p>
                    )}
                  </li>
                ))}
              </ul>
              {(esito.righe_senza_data > 0 ||
                esito.mesi.some((m) => m.tipo_ignoto || m.estere || m.iva_marketplace)) && (
                <ul className="mt-2 space-y-1 text-amber">
                  {esito.righe_senza_data > 0 && (
                    <li>
                      {esito.righe_senza_data} righe senza data di spedizione leggibile: non
                      attribuite a nessun mese.
                    </li>
                  )}
                  {esito.mesi
                    .filter((m) => m.tipo_ignoto || m.estere || m.iva_marketplace)
                    .map((m) => (
                      <li key={m.chiave}>
                        {m.etichetta}:{" "}
                        {m.tipo_ignoto > 0 && `${m.tipo_ignoto} righe di tipo non riconosciuto. `}
                        {m.estere > 0 && `${m.estere} righe estere nei corrispettivi italiani. `}
                        {m.iva_marketplace > 0 &&
                          `${m.iva_marketplace} righe con IVA a carico del marketplace. `}
                        Da controllare a mano.
                      </li>
                    ))}
                </ul>
              )}
            </>
          )}
        </section>
      )}

      <h2 className="mb-2 font-display text-[17px] font-bold">Mesi generati</h2>
      {storico === null ? (
        <p className="text-[12.5px] text-ink-2">Carico...</p>
      ) : storico.length === 0 ? (
        <p className="text-[12.5px] text-ink-2">
          Nessun mese ancora generato. Carica il primo tax report qui sopra.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line bg-paper">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-line text-left text-ink-2">
                <th className="px-3 py-2 font-medium">Mese</th>
                <th className="px-3 py-2 font-medium">Date coperte</th>
                <th className="px-3 py-2 text-right font-medium">Corrispettivi</th>
                <th className="px-3 py-2 text-right font-medium">Rimborsi</th>
                <th className="px-3 py-2 text-right font-medium">Righe</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {storico.map((m) => (
                <tr key={m.chiave} className="border-b border-line-2 last:border-0">
                  <td className="px-3 py-2 font-medium">{m.etichetta}</td>
                  <td className="px-3 py-2 text-ink-2">
                    {giorno(m.prima_data)} → {giorno(m.ultima_data)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{euro(m.totale_positivi)} €</td>
                  <td className="px-3 py-2 text-right tabular-nums">{euro(m.totale_rimborsi)} €</td>
                  <td className="px-3 py-2 text-right tabular-nums text-ink-2">{m.righe}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      className="rounded-md border border-line bg-card px-2.5 py-1 text-[11.5px] text-ink-2 hover:border-accent"
                      onClick={() => {
                        void scaricaMese(m).catch((err) => setErrore(prettifyApiError(err)));
                      }}
                    >
                      Scarica
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
